import { FormattedMessage, useIntl } from 'react-intl';

import { getSalaryFormatOptions } from '@xing-com/crate-jobs-helpers';
import type { SalaryEstimateValid } from '@xing-com/crate-jobs-helpers';
import { IconInfo } from '@xing-com/icons';
import { usePopOver } from '@xing-com/pop-over';

import { ModuleTitle } from '../../module-title';
import { SalaryForecastBar } from '../salary-forecast-bar';

import {
  BarColorLegend,
  BarColorLegendContainer,
  BarColorLegendSample,
  BarColorLegendText,
  InfoButton,
  InfoCopy,
  InfoPopover,
  InfoPopoverCopy,
} from './salary-estimation.styles';

const getMedianOffset = (
  minimum: number,
  maximum: number,
  median: number
): number => ((median - minimum) * 100) / (maximum - minimum);

type Props = {
  salary: SalaryEstimateValid;
};

export const SalaryEstimation: React.FC<Props> = ({ salary }) => {
  const { formatNumber, formatMessage } = useIntl();
  const { maximum, minimum, median, currency } = salary;
  const formatOptions = getSalaryFormatOptions(currency);
  const hasMedian = typeof median === 'number';
  const medianValue = hasMedian
    ? formatNumber(median, formatOptions)
    : undefined;
  const leftRangeValue = formatNumber(minimum, formatOptions);
  const rightRangeValue = formatNumber(maximum, formatOptions);
  const barPosition = hasMedian
    ? getMedianOffset(minimum, maximum, median)
    : undefined;

  const popOver = usePopOver();
  const a11yModuleLabel = `${formatMessage({
    id: 'JOBS_JOB_DETAIL_SALARY_ESTIMATED_HEADLINE',
  })}: ${formatMessage(
    {
      id: hasMedian
        ? 'JOBS_JOB_DETAIL_SALARY_MODULE_A11Y_RANGE_WITH_MEDIAN'
        : 'JOBS_JOB_DETAIL_SALARY_MODULE_A11Y_RANGE',
    },
    {
      maximum: rightRangeValue,
      minimum: leftRangeValue,
      median: medianValue,
    }
  )}`;

  return (
    <>
      <ModuleTitle
        titleKey="JOBS_JOB_DETAIL_SALARY_ESTIMATED_HEADLINE"
        titleAriaLabel={a11yModuleLabel}
        noMargin
      />
      <InfoButton
        $hasMedian={hasMedian}
        size="small"
        ref={popOver.triggerRef}
        onClick={popOver.togglePopOver}
        icon={IconInfo}
      >
        <InfoCopy size="small" lineHeight="dense">
          <FormattedMessage id="JOBS_JOB_DETAIL_SALARY_ESTIMATED_INFO" />
        </InfoCopy>
      </InfoButton>
      <InfoPopover
        isInFlow
        show={popOver.show}
        showCloseButton={true}
        onOutsideClick={popOver.handleHide}
        triggerRef={popOver.triggerRef}
        aria-label={formatMessage({
          id: 'JOBS_JOB_DETAIL_SALARY_ESTIMATED_INFO_CONTENT',
        })}
      >
        <InfoPopoverCopy size="small" aria-hidden>
          <FormattedMessage id="JOBS_JOB_DETAIL_SALARY_ESTIMATED_INFO_CONTENT" />
        </InfoPopoverCopy>
      </InfoPopover>

      <SalaryForecastBar
        headlineCopy={medianValue}
        legend={[leftRangeValue, rightRangeValue]}
        barPosition={barPosition}
        isFullRange={!hasMedian}
      />
      <BarColorLegendContainer aria-hidden>
        {hasMedian ? (
          <BarColorLegend>
            <BarColorLegendSample $isAverage />
            <BarColorLegendText size="small" aria-hidden>
              <FormattedMessage id="JOBS_JOB_DETAIL_SALARY_ESTIMATED_AVERAGE_LEGEND" />
            </BarColorLegendText>
          </BarColorLegend>
        ) : null}
        <BarColorLegend>
          <BarColorLegendSample $isAverage={!hasMedian} />
          <BarColorLegendText size="small" aria-hidden>
            <FormattedMessage id="JOBS_JOB_DETAIL_SALARY_ESTIMATED_RANGE_LEGEND" />
          </BarColorLegendText>
        </BarColorLegend>
      </BarColorLegendContainer>
    </>
  );
};
